/**
 * IBAN / SWIFT (BIC) checks shared by the profile form and the API (ISO 13616 / ISO 9362).
 * Stored values are normalised: upper case, no spaces.
 */

/** Upper case, spaces and dashes removed. */
export const normalizeIban = (value: string): string => value.replace(/[\s-]/g, '').toUpperCase()

/** ISO 13616 shape: 2 letters, 2 check digits, 11–30 alphanumerics (15–34 characters). */
const IBAN_SHAPE = /^[A-Z]{2}[0-9]{2}[A-Z0-9]{11,30}$/

/** Country-specific lengths for the countries we actually see; others only get the generic shape. */
const IBAN_LENGTH: Record<string, number> = {
    EE: 20,
    FI: 18,
    LV: 21,
    LT: 20,
    DE: 22,
    NL: 18,
    SE: 24,
    GB: 22,
    FR: 27,
    ES: 24,
    IT: 27,
    PL: 28,
    UA: 29,
}

/** Mod-97 check: move the first four characters to the end, letters → 10..35, remainder must be 1. */
const mod97 = (iban: string): number => {
    const rearranged = iban.slice(4) + iban.slice(0, 4)
    let remainder = 0
    for (const ch of rearranged) {
        const piece = ch >= 'A' && ch <= 'Z' ? String(ch.charCodeAt(0) - 55) : ch
        for (const digit of piece) remainder = (remainder * 10 + Number(digit)) % 97
    }
    return remainder
}

/** True for a well-formed IBAN with a valid checksum (input may contain spaces / lower case). */
export const isValidIban = (value: string): boolean => {
    const iban = normalizeIban(value)
    if (!IBAN_SHAPE.test(iban)) return false
    const expected = IBAN_LENGTH[iban.slice(0, 2)]
    if (expected !== undefined && iban.length !== expected) return false
    return mod97(iban) === 1
}

/** Groups of four for reading ("EE38 2200 2210 2014 5685"); leaves invalid input as typed. */
export const formatIban = (value: string): string => {
    const iban = normalizeIban(value)
    return IBAN_SHAPE.test(iban) ? (iban.match(/.{1,4}/g) ?? [iban]).join(' ') : value
}

export const normalizeSwift = (value: string): string => value.replace(/\s/g, '').toUpperCase()

/** ISO 9362: 4-letter bank, 2-letter country, 2 alphanumerics, optional 3-character branch. */
const SWIFT_SHAPE = /^[A-Z]{6}[A-Z0-9]{2}([A-Z0-9]{3})?$/

export const isValidSwift = (value: string): boolean => SWIFT_SHAPE.test(normalizeSwift(value))
